import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FamilyApprovedPersonDto, ISharingPeriod, PeriodEnum } from '../../type/twoFactor';
import { useUtil } from '../../screen/HouseholdDashboard/component/BottomCard/util';
import { filterPersonsOlderThanEighteen } from '../../screen/HouseholdDashboard/component/BottomCard/BottomCard';
import { PersonInHouseholdDto } from 'graphql/person/generates';
import { useGetMultipleHouseHoldPerPerson } from '../../hooks/useGetMultipleHouseHoldPerPerson';
import { useFamilyApprovalTwoFaPersonCentric } from '../../hooks/useFamilyApprovalTwoFaPersonCentric';
import { useFamilyApprovalTwoFa } from '../../hooks/useFamilyApprovalTwoFa';
import { useGetAdvisor } from '../../hooks/useGetAdvisor';
import { useFamilyApproval } from '../../hooks/useFamilyApproval';
import { useFamilyApprovalPersonCentric } from '../../hooks/useFamilyApprovalPersonCentric';
import FamilyApprovalWidget from '@dvag/family-approval-widget';
import { SentApprovalState, TransactionStatusType, VersionType } from 'type/familyApproval';
import { useFamilyApprovalTransaction } from 'hooks/useFamilyApprovalTransaction';
import { Advisor } from 'type/advisor';
import { useGetApprovalTransactionStatus } from 'hooks/useGetApprovalTransactionStatus';
import { useNotification } from 'utils/notification/NotificationContext';
import { useQrApprovalContext } from './QrApprovalContext';
import { permissions } from '@dvag/dfs-constant-config';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { getMonitoring } from '../../service/getMonitoring';

type Props = { personList: PersonInHouseholdDto[]; householdId: string | undefined };

export type FamilyApprovedPerson = FamilyApprovedPersonDto & {
  firstName?: string;
};

interface ApprovalInterface {
  approvalDuration?: ISharingPeriod;
  selectedForApprovalPersons?: FamilyApprovedPerson[];
}

export const FamilyApproval = ({ personList, householdId }: Props) => {
  const [sentApprovalState, setSentApprovalState] = useState<SentApprovalState | undefined>(
    undefined,
  );
  const [transactionStatus, setTransactionStatus] = useState<TransactionStatusType | undefined>(
    undefined,
  );
  const [transactionUrl, setTransactionUrl] = useState<string | undefined>(undefined);
  const { qrApprovalValue, setQrApprovalValue, qrKeycloakRef, setQrKeycloakRef } =
    useQrApprovalContext();
  const [pollingUrl, setPollingUrl] = useState<string | undefined>(undefined);

  const prevKeycloakIdRef = useRef<{ kundenNr: string; keycloakId: string }>({
    kundenNr: '',
    keycloakId: '',
  });

  const isNewKundenportalShown = useFeatureIsOn(permissions.canAccessNewKundenportalFlag);
  const isNewFamilyApproval = useFeatureIsOn(permissions.canAccessNewClientPortalFlag);
  const isQrFamilyApproval = useFeatureIsOn(permissions.canAccessQrFamilyApproval);

  const firstPersonId = useMemo(() => personList[0]?.id, [personList]);

  const { data: advisorInfo } = useGetAdvisor(householdId);
  const sendFamilyApproval = useFamilyApproval();
  const sendFamilyApprovalPersonCentric = useFamilyApprovalPersonCentric();
  const { mutateAsync, abort } = useFamilyApprovalTwoFa();
  const { mutateAsync: mutateTwoFaPersonCentric, abort: abortTwoFaPersonCentric } =
    useFamilyApprovalTwoFaPersonCentric();
  const { mutateAsync: mutateTwoFaTransaction, abort: abortTwoFaTransaction } =
    useFamilyApprovalTransaction();
  const { getTransactionStatus } = useGetApprovalTransactionStatus({
    pollingUrl: pollingUrl ?? '',
  });

  const { showError } = useNotification();

  const { data: multipleHouseholdPerPersonList, refetch: refetchMultipleHouseHold } =
    useGetMultipleHouseHoldPerPerson(
      householdId,
      firstPersonId,
      !!firstPersonId && isNewFamilyApproval,
    );

  const personListOlderEighteen: FamilyApprovedPersonDto[] = useMemo(() => {
    const personsOlder18 = filterPersonsOlderThanEighteen(personList, isNewFamilyApproval);
    return personsOlder18.map((item) => {
      const match = multipleHouseholdPerPersonList?.find((a) => a.clientId === item.id);

      return {
        ...item,
        ...(match
          ? {
              personRegistered: match.personRegistered,
              multipleHousehold: match.multipleHousehold,
            }
          : {}),
      };
    });
  }, [personList, isNewFamilyApproval, multipleHouseholdPerPersonList]);

  const isTwoPersonsOlderEighteen = useMemo(
    () => personListOlderEighteen?.length > 1 && isNewKundenportalShown,
    [personListOlderEighteen?.length, isNewKundenportalShown],
  );

  const { isKundenzugangToolClosed, setIsKundenzugangToolClosed, customerDetail } = useUtil(
    householdId as string,
    isTwoPersonsOlderEighteen,
  );

  const personListOlderEighteenMap = useMemo(() => {
    return new Map(personListOlderEighteen.map((person) => [person.id, person]));
  }, [personListOlderEighteen]);

  const isMultipleHousehold = useMemo(() => {
    if (!customerDetail?.kundenNr) {
      return false;
    }

    return personListOlderEighteenMap.get(customerDetail.kundenNr)?.multipleHousehold ?? false;
  }, [customerDetail, personListOlderEighteenMap]);

  const customerPerson = useMemo(
    () => personList.find((item) => item.id === customerDetail?.kundenNr),
    [customerDetail, personList],
  );

  const mutateSendApproval = useCallback(
    async ({
      keycloakRef,
      selectedForApprovalPersons,
      personListOlderEighteen,
      value,
      householdId,
      advisorInfo,
    }: {
      keycloakRef: {
        kundenNr: string;
        keycloakId: string;
      };
      selectedForApprovalPersons: FamilyApprovedPersonDto[];
      personListOlderEighteen: FamilyApprovedPersonDto[];
      value: {
        approvalDuration?: ISharingPeriod;
        selectedForApprovalPersons?: FamilyApprovedPersonDto[];
      };
      householdId: string | undefined;
      advisorInfo: Advisor | undefined;
    }) => {
      if (isNewFamilyApproval) {
        return mutateTwoFaPersonCentric({
          keycloakId: keycloakRef?.keycloakId,
          personListOlderEighteen: selectedForApprovalPersons,
        });
      } else {
        return mutateAsync({
          householdId,
          keycloakId: keycloakRef?.keycloakId,
          customerPersonId: keycloakRef?.kundenNr,
          personListOlderEighteen: personListOlderEighteen,
          duration: value.approvalDuration || {
            value: 'UNLIMITED',
            label: 'Dauerhaft',
          },
          advisorId: advisorInfo?.id,
        });
      }
    },
    [isNewFamilyApproval],
  );

  const handleSendApprovalQrVersion = useCallback(
    async ({
      keycloakRef,
      selectedForApprovalPersons,
      value,
    }: {
      keycloakRef: {
        kundenNr: string;
        keycloakId: string;
      };
      selectedForApprovalPersons: FamilyApprovedPersonDto[];
      value: ApprovalInterface;
    }) => {
      const response = await mutateTwoFaTransaction({
        keycloakId: keycloakRef?.keycloakId,
        personListOlderEighteen: selectedForApprovalPersons,
      });
      if (response.status) {
        if (response.status === 'ABGELEHNT') {
          setTransactionStatus('ABGELEHNT');
        } else if (response.status === 'OFFEN') {
          setTransactionStatus('OFFEN');
        } else if (response.status === 'TIMEOUT') {
          setTransactionStatus('TIMEOUT');
        }
      } else {
        setQrKeycloakRef(keycloakRef);
        setQrApprovalValue(value);
        setTransactionUrl(response?.transactionUrl);
        setPollingUrl(response?.pollingUrl);
      }
    },
    [mutateTwoFaTransaction],
  );

  const handleSendApprovalStandardVersion = useCallback(
    async ({
      keycloakRef,
      selectedForApprovalPersons,
      personListOlderEighteen,
      value,
      householdId,
      advisorInfo,
    }: {
      keycloakRef: {
        kundenNr: string;
        keycloakId: string;
      };
      selectedForApprovalPersons: FamilyApprovedPersonDto[];
      personListOlderEighteen: FamilyApprovedPersonDto[];
      value: {
        approvalDuration?: ISharingPeriod;
        selectedForApprovalPersons?: FamilyApprovedPersonDto[];
      };
      householdId: string | undefined;
      advisorInfo: Advisor | undefined;
    }) => {
      const response = await mutateSendApproval({
        keycloakRef,
        selectedForApprovalPersons,
        personListOlderEighteen,
        value,
        householdId,
        advisorInfo,
      });
      switch (response?.status) {
        case 'AKZEPTIERT': {
          setSentApprovalState('success');
          if (!isNewFamilyApproval && value.approvalDuration) {
            sendFamilyApproval.mutate(
              {
                householdId,
                customerPersonId: keycloakRef?.kundenNr,
                duration: value.approvalDuration,
              },
              {
                onError: () => setSentApprovalState('failed'),
              },
            );
          }
          if (isNewFamilyApproval) {
            const data = value.selectedForApprovalPersons?.map((item) => {
              const approvalDuration = item.sharingPeriod?.value ?? PeriodEnum.UNLIMITED;
              return {
                approvalDuration,
                approvedClientId: item.id,
              };
            });
            sendFamilyApprovalPersonCentric.mutate(
              {
                data,
                householdId,
                customerPersonId: keycloakRef?.kundenNr,
              },
              {
                onError: () => setSentApprovalState('failed'),
              },
            );
          }
          break;
        }
        case 'ABGELEHNT':
          setSentApprovalState('failed');
          break;
        case 'OFFEN':
          setSentApprovalState('retry');
          break;
        default:
          setSentApprovalState('failed');
      }
    },
    [sendFamilyApproval, sendFamilyApprovalPersonCentric, isNewFamilyApproval],
  );

  const onSendApproval = useCallback(
    async (value: {
      approvalDuration?: ISharingPeriod;
      selectedForApprovalPersons?: FamilyApprovedPerson[];
    }) => {
      try {
        setSentApprovalState(undefined);
        setTransactionStatus(undefined);
        const keycloakRef = prevKeycloakIdRef.current;

        const selectedForApprovalPersons = value?.selectedForApprovalPersons || [];

        if (isQrFamilyApproval) {
          await handleSendApprovalQrVersion({ keycloakRef, selectedForApprovalPersons, value });
        } else {
          await handleSendApprovalStandardVersion({
            keycloakRef,
            selectedForApprovalPersons,
            personListOlderEighteen,
            householdId,
            advisorInfo,
            value,
          });
        }
      } catch (err) {
        if (isQrFamilyApproval) {
          setTransactionStatus('OFFEN');
        }
        getMonitoring().trackException(err as Error);
      }
    },
    [
      advisorInfo?.id,
      householdId,
      personListOlderEighteen,
      isNewFamilyApproval,
      isQrFamilyApproval,
    ],
  );

  useEffect(() => {
    if (sentApprovalState === 'success' && qrApprovalValue && qrKeycloakRef && isQrFamilyApproval) {
      const data = qrApprovalValue.selectedForApprovalPersons?.map((item) => ({
        approvalDuration: item.sharingPeriod?.value ?? PeriodEnum.UNLIMITED,
        approvedClientId: item.id,
      }));

      sendFamilyApprovalPersonCentric.mutate({
        data,
        householdId,
        customerPersonId: qrKeycloakRef.kundenNr,
      });
    }
  }, [sentApprovalState, qrApprovalValue, qrKeycloakRef, isQrFamilyApproval]);

  const onRequestApprovalStatus = useCallback(async () => {
    if (isQrFamilyApproval) {
      try {
        setSentApprovalState(undefined);
        const approvalTransactionStatus = await getTransactionStatus();
        const retryMessage = `${customerPerson?.firstName} ${customerPerson?.lastName} muss die Familienfreigabe auf dem Smartphone bestätigen, bevor Sie fortfahren können.`;

        switch (approvalTransactionStatus?.status) {
          case 'AKZEPTIERT':
            setSentApprovalState('success');
            break;
          case 'ABGELEHNT':
            setSentApprovalState('rejected');
            break;
          case 'OFFEN':
            showError({
              noTitle: true,
              description: retryMessage,
            });
            break;
          case 'TIMEOUT':
            setSentApprovalState('retry');
            break;
          default:
            setSentApprovalState('failed');
        }
      } catch (e) {
        setSentApprovalState('failed');
        getMonitoring().trackException(e as Error);
      }
    }
  }, [isQrFamilyApproval, pollingUrl, customerPerson]);

  useEffect(() => {
    if (isKundenzugangToolClosed) {
      refetchMultipleHouseHold();
    }
    if (!isKundenzugangToolClosed) {
      if (isQrFamilyApproval) {
        abortTwoFaTransaction();
      } else if (isNewFamilyApproval) {
        abortTwoFaPersonCentric();
      } else {
        abort();
      }
    }
  }, [isKundenzugangToolClosed, refetchMultipleHouseHold, isNewFamilyApproval, isQrFamilyApproval]);

  useEffect(() => {
    prevKeycloakIdRef.current = customerDetail;
  }, [customerDetail]);

  let widgetVersionType: VersionType;
  if (isQrFamilyApproval) {
    widgetVersionType = 'qrCodeVersion';
  } else if (isNewFamilyApproval) {
    widgetVersionType = 'newVersion';
  } else {
    widgetVersionType = 'oldVersion';
  }

  if (isTwoPersonsOlderEighteen && customerPerson && !isMultipleHousehold) {
    return (
      <FamilyApprovalWidget
        sentApprovalState={sentApprovalState}
        customerPerson={customerPerson}
        onClick={onSendApproval}
        onQrClick={onRequestApprovalStatus}
        isOpen={isKundenzugangToolClosed}
        setIsOpen={setIsKundenzugangToolClosed}
        data-testid="family-approval-widget"
        personListWithConsent={personListOlderEighteen}
        activeVersion={widgetVersionType}
        transactionStatus={transactionStatus}
        transactionUrl={transactionUrl}
      />
    );
  }
};
